import React, { useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PaymentsIcon from "@mui/icons-material/Payments";
import MemoryIcon from "@mui/icons-material/Memory";
import { companyInfoData, productsData } from "../services/apiService";
import { SEO } from "../components/SEO";
import {
  AnimatedInView,
  Section,
  GradientHero,
  GradientText,
  GlassCard,
  StatCounter,
  CTABand,
  BrandStrip,
  TestimonialCard,
  ProductCard,
  GoogleRatingBadge,
  staggerChild,
} from "../components/ui";
import { useGoogleRating } from "../hooks/useGoogleRating";
import type { TestimonialData } from "../components/ui";
import { motion } from "framer-motion";
import {
  generateLocalBusinessStructuredData,
  generateWebsiteStructuredData,
} from "../utils/structuredData";
import {
  REACT_APP_SITE_URL,
  SITE_NAME,
  SITE_DESCRIPTION,
  SITE_IMAGE,
} from "../config/seo";

const SERVICE_ICONS = [
  <PointOfSaleIcon fontSize="large" />,
  <StorefrontIcon fontSize="large" />,
  <ReceiptLongIcon fontSize="large" />,
  <MemoryIcon fontSize="large" />,
  <PaymentsIcon fontSize="large" />,
];

const localBusinessJsonLd = generateLocalBusinessStructuredData({
  name: "R S Enterprises",
  description: SITE_DESCRIPTION,
  url: REACT_APP_SITE_URL,
  address: {
    streetAddress:
      "1st Floor, Office No. 106-107, I.J Complex, G-29, Near Subhash Chowk, Opp. Metro Pillar No. 42",
    addressLocality: "Laxmi Nagar",
    addressRegion: "Delhi",
    postalCode: "110092",
    addressCountry: "IN",
  },
  geo: {
    latitude: "28.6317",
    longitude: "77.2770",
  },
  telephone: "+91-9013573235",
  openingHours: ["Mo-Sa 10:00-18:00"],
  priceRange: "₹₹",
  image: SITE_IMAGE,
});

const Home: React.FC = () => {
  // Static data imported synchronously — keeps prerendered HTML and the first
  // client render identical (no hydration mismatch, no loading flash).
  const companyInfo = companyInfoData;
  const featured = productsData.filter((p) => p.isFeatured).slice(0, 4);
  const { data: googleRating } = useGoogleRating();
  const { hash } = useLocation();

  // Arriving via a section hash (e.g. the footer's /#services link): scroll to
  // that section. Delayed a tick so the entrance animations and layout have
  // settled before measuring scroll position.
  useEffect(() => {
    if (!hash) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById(hash.slice(1))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [hash]);

  // Prefer live Google reviews; fall back to the placeholder testimonials
  const testimonials: TestimonialData[] =
    googleRating && googleRating.reviews.length > 0
      ? googleRating.reviews.map((review) => ({
          quote: review.text,
          name: review.author,
          business: "Google Review",
          location: review.relativeTime,
          rating: review.rating,
        }))
      : companyInfo.testimonials;

  return (
    <>
      <SEO
        title={SITE_NAME}
        description={SITE_DESCRIPTION}
        jsonLd={[localBusinessJsonLd, generateWebsiteStructuredData()]}
      />

      {/* 1 — Hero */}
      <GradientHero
        eyebrow="Since 2011 · Delhi, India"
        title={
          <>
            Billing & POS solutions <GradientText>that keep business moving</GradientText>
          </>
        }
        subtitle="ECR machines, POS systems, thermal printers and billing software from trusted brands — with genuine pricing, expert setup and support that answers the phone."
        primaryCta={{ label: "Get in Touch", to: "/contact#contact-form" }}
        secondaryCta={{
          label: "WhatsApp Us",
          href: "https://wa.me/919013573235?text=Hi%2C%20I%27d%20like%20to%20know%20more%20about%20your%20billing%20and%20POS%20products.",
          icon: <WhatsAppIcon />,
        }}
        children={
          googleRating ? (
            <Box sx={{ mt: 3 }}>
              <GoogleRatingBadge data={googleRating} tone="dark" />
            </Box>
          ) : null
        }
        rightSlot={
          <GlassCard tone="dark" sx={{ p: 4, textAlign: "center" }}>
            <Box
              component="video"
              src="/images/hero.mp4"
              poster="/images/products/pos815j.png"
              autoPlay
              muted
              loop
              playsInline
              aria-label="Billing and POS systems showcase"
              sx={{
                width: "100%",
                maxWidth: 360,
                borderRadius: 3,
                display: "block",
                mx: "auto",
                filter: "drop-shadow(0 24px 40px rgba(0,0,0,0.4))",
              }}
            />
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2, flexWrap: "wrap" }}>
              <Chip label="GST Ready" size="small" sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "common.white" }} />
              <Chip label="Same-day Setup" size="small" sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "common.white" }} />
              <Chip label="Pan-India Delivery" size="small" sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "common.white" }} />
            </Box>
          </GlassCard>
        }
      />

      {/* 2 — Brand strip */}
      <Section py={{ xs: 4, md: 5 }}>
        <AnimatedInView variant="fade">
          <BrandStrip />
        </AnimatedInView>
      </Section>

      {/* 3 — Stats band */}
      {companyInfo && (
        <Section bg="navy" py={{ xs: 5, md: 7 }}>
          <AnimatedInView variant="stagger">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                gap: { xs: 3, md: 4 },
              }}
            >
              {companyInfo.stats.map((stat) => (
                <motion.div key={stat.label} variants={staggerChild}>
                  <GlassCard tone="dark" sx={{ height: "100%" }}>
                    <StatCounter value={stat.value} suffix={stat.suffix} label={stat.label} />
                  </GlassCard>
                </motion.div>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      {/* 4 — Featured products */}
      {featured.length > 0 && (
        <Section id="featured-products">
          <AnimatedInView>
            <Typography variant="overline" component="p" sx={{ color: "secondary.dark" }}>
              Best Sellers
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 2,
                mb: 4,
              }}
            >
              <Typography variant="h2" component="h2">
                Featured products
              </Typography>
              <Button
                component={RouterLink}
                to="/products"
                endIcon={<ArrowForwardIcon />}
                sx={{ fontWeight: 700 }}
              >
                View all products
              </Button>
            </Box>
          </AnimatedInView>
          <AnimatedInView variant="stagger">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 3,
              }}
            >
              {featured.map((product) => (
                <motion.div key={product.id} variants={staggerChild} style={{ height: "100%" }}>
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      {/* 5 — Services */}
      {companyInfo && (
        <Section bg="paper" id="services">
          <AnimatedInView>
            <Typography variant="overline" component="p" sx={{ color: "secondary.dark" }}>
              What We Do
            </Typography>
            <Typography variant="h2" component="h2" sx={{ mb: 4 }}>
              Our services
            </Typography>
          </AnimatedInView>
          <AnimatedInView variant="stagger">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 3,
              }}
            >
              {companyInfo.services.map((service, index) => (
                <motion.div key={service.name} variants={staggerChild} style={{ height: "100%" }}>
                  <GlassCard tone="light" sx={{ height: "100%" }}>
                    <Box sx={{ color: "secondary.main", mb: 1.5 }}>
                      {SERVICE_ICONS[index % SERVICE_ICONS.length]}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 700 }}>
                      {service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {service.description}
                    </Typography>
                  </GlassCard>
                </motion.div>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      {/* 6 — Why choose us */}
      {companyInfo && (
        <Section id="why-choose-us">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
              gap: { xs: 4, md: 8 },
              alignItems: "center",
            }}
          >
            <AnimatedInView>
              <Typography variant="overline" component="p" sx={{ color: "secondary.dark" }}>
                Why Choose Us
              </Typography>
              <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
                A partner, not just a supplier
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {companyInfo.description}
              </Typography>
              <Button
                component={RouterLink}
                to="/about"
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
              >
                About our company
              </Button>
            </AnimatedInView>
            <AnimatedInView delay={0.15}>
              <List>
                {companyInfo.whyChooseUs?.map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircleIcon sx={{ color: "secondary.main" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: "body1", fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </AnimatedInView>
          </Box>
        </Section>
      )}

      {/* 7 — Testimonials (live Google reviews when available) */}
      {testimonials.length > 0 && (
        <Section bg="paper" id="testimonials">
          <AnimatedInView>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="overline" component="p" sx={{ color: "secondary.dark" }}>
                Testimonials
              </Typography>
              <GoogleRatingBadge data={googleRating} tone="light" />
            </Box>
            <Typography variant="h2" component="h2" sx={{ mb: 4 }}>
              {googleRating && googleRating.reviews.length > 0
                ? "What our customers say on Google"
                : "What our customers say"}
            </Typography>
          </AnimatedInView>
          <AnimatedInView variant="stagger">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
              }}
            >
              {testimonials.map((testimonial) => (
                <motion.div key={testimonial.name} variants={staggerChild} style={{ height: "100%" }}>
                  <TestimonialCard testimonial={testimonial} />
                </motion.div>
              ))}
            </Box>
          </AnimatedInView>
        </Section>
      )}

      {/* 8 — CTA */}
      <CTABand />
    </>
  );
};

export default Home;
